import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BirthDatePicker,
  Col,
  Form,
  FormActions,
  Modal,
  NumberInput,
  Row,
  SelectInput,
  TextInput,
  Title,
  useBirthDateValidation,
} from 'ui';
import { timezones } from 'common/utils';

import type { Gender, Option, PersonalData } from '../../types';
import { genders, imperialUnits, languages } from '../../utils';

export interface PersonalFormData extends Omit<PersonalData, 'heightInImperial'> {
  heightInImperial: { feet: number; inches: number };
}

type P = {
  initialData?: PersonalFormData;
  onBack: () => void;
  onSubmit: (values: PersonalFormData) => void;
  user: { firstName?: string; gender?: Gender; lastName?: string };
};

const generateInitialValues = (
  user: { firstName?: string; gender?: Gender; lastName?: string },
  initialData?: PersonalFormData,
) => {
  const initialValues: Partial<PersonalFormData> = {
    firstName: user.firstName,
    gender: user.gender,
    lastName: user.lastName,
    prefferedLanguage: 'en_US',
    prefferedTimezone: 'America/Chicago',
    ...initialData,
  };

  return initialValues;
};

const PersonalInfo = ({ initialData, onBack, onSubmit, user }: P) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<PersonalFormData>();

  const initialValues: Partial<PersonalFormData> = generateInitialValues(user, initialData);

  const prefferedLanguages: Option[] = languages;
  const prefferedTimezones: Option[] = timezones;

  const handleFinish = (values: PersonalFormData) => {
    onSubmit(values);
  };

  const genderOptions: Option[] = useMemo(
    () => genders.map((gender) => ({ label: t(gender.label), value: gender.value })),
    [t],
  );

  const birthdateValidatorRule = useBirthDateValidation();

  return (
    <Form<PersonalFormData> form={form} initialValues={initialValues} onFinish={handleFinish}>
      <fieldset>
        <legend>
          <Title level={4} marginBottom>
            Personal information
          </Title>
        </legend>
        <Row gutter={24}>
          <Col className="personal-info__col" md={{ span: 12 }} xs={{ span: 24 }}>
            <TextInput
              input={{
                autoComplete: 'given-name',
              }}
              item={{
                label: 'First name',
                name: 'firstName',
                rules: [
                  {
                    required: true,
                  },
                ],
              }}
            />
          </Col>
          <Col md={{ span: 12 }} xs={{ span: 24 }}>
            <TextInput
              input={{
                autoComplete: 'family-name',
              }}
              item={{
                label: 'Last name',
                name: 'lastName',
                rules: [
                  {
                    required: true,
                  },
                ],
              }}
            />
          </Col>
          <Col span={24}>
            <Form.Item<PersonalFormData>
              label="Date of birth"
              name="birthdate"
              rules={[birthdateValidatorRule({ isRequired: true }), { required: true }]}
            >
              <BirthDatePicker />
            </Form.Item>
          </Col>
          <Col span={24}>
            <SelectInput
              input={{
                getOptionLabel: (option) => option.label,
                getOptionValue: (option) => option.value,
                options: prefferedTimezones,
                showSearch: true,
              }}
              item={{
                'data-test-id': 'select-preffered-timezone',
                label: 'My timezone',
                name: 'prefferedTimezone',
                rules: [
                  {
                    required: true,
                  },
                ],
              }}
            />
          </Col>
          <Col span={24}>
            <SelectInput
              input={{
                getOptionLabel: (option) => option.label,
                getOptionValue: (option) => option.value,
                options: genderOptions,
              }}
              item={{
                'data-test-id': 'select-gender',
                label: 'Gender',
                name: 'gender',
                rules: [
                  {
                    required: true,
                  },
                ],
              }}
            />
          </Col>
          <Col md={{ span: 12 }} xs={{ span: 24 }}>
            <NumberInput
              input={{
                controls: false,
                max: 1323,
                min: 1,
                suffix: imperialUnits.units.weight.label,
              }}
              item={{
                label: 'Weight',
                name: 'weightInImperial',
                rules: [
                  {
                    required: true,
                  },
                ],
              }}
            />
          </Col>
          <Col md={{ span: 6 }} xs={{ span: 12 }}>
            <SelectInput
              input={{
                getOptionLabel: (option) => option.toString(),
                getOptionValue: (option) => option,
                onChange() {
                  form.setFieldValue(['heightInImperial', 'inches'], undefined);
                },
                options: [...Array.from({ length: 10 }).keys()],
              }}
              item={{
                label: 'Height (ft)',
                name: ['heightInImperial', 'feet'],
                rules: [
                  {
                    required: true,
                  },
                ],
              }}
            />
          </Col>
          <Col md={{ span: 6 }} xs={{ span: 12 }}>
            <Form.Item<PersonalFormData> dependencies={[['heightInImperial', 'feet']]} noStyle>
              {() => {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
                const feet = form.getFieldValue(['heightInImperial', 'feet']);
                const inchesOptions =
                  feet === 0
                    ? Array.from({ length: 8 }, (_, index) => index + 4)
                    : // eslint-disable-next-line sonarjs/no-nested-conditional -- TODO: fix later
                      feet === 9
                      ? [...Array.from({ length: 11 }).keys()]
                      : [...Array.from({ length: 12 }).keys()];

                return (
                  <SelectInput
                    input={{
                      getOptionLabel: (option) => option.toString(),
                      getOptionValue: (option) => option,
                      options: inchesOptions,
                    }}
                    item={{
                      label: 'Height (in)',
                      name: ['heightInImperial', 'inches'],
                      rules: [
                        {
                          required: true,
                        },
                      ],
                    }}
                  />
                );
              }}
            </Form.Item>
          </Col>
          <Col span={24}>
            <SelectInput
              input={{
                getOptionLabel: (option) => option.label,
                getOptionValue: (option) => option.value,
                options: prefferedLanguages,
                showSearch: true,
              }}
              item={{
                'data-test-id': 'select-preffered-language',
                label: 'Language',
                name: 'prefferedLanguage',
                rules: [
                  {
                    required: true,
                  },
                ],
              }}
            />
          </Col>
        </Row>
      </fieldset>
      <Modal.HugContent.Footer>
        <FormActions right={{ onClick: onBack }} submit={{ children: 'Continue' }} />
      </Modal.HugContent.Footer>
    </Form>
  );
};

export default PersonalInfo;
